import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { LayoutGrid, List, Plus, Search, X, SlidersHorizontal, ChevronRight, Check, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import TaskListView from "../components/tasks/TaskListView";
import TaskKanbanView from "../components/tasks/TaskKanbanView";
import NewTaskModal from "../components/tasks/NewTaskModal";
import ProjectSelector from "../components/tasks/ProjectSelector";
import ProjectManagerSheet from "../components/tasks/projects/ProjectManagerSheet";
import ProjectForm, {
  projectFormToast,
} from "../components/tasks/projects/ProjectForm";
import MoveTaskDialog, {
  type MoveTaskMode,
  type MoveTaskPayload,
} from "../components/tasks/MoveTaskDialog";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import type { Task, TaskStatusDetail, TaskAssignee, TaskPriority } from "../utils/taskHelpers";
import { PRIORITY_DISPLAY_MAP, PRIORITY_API_MAP, generateSessionKey, isValidTaskStatusDetail } from "../utils/taskHelpers";
import type { TaskProject, TaskScope } from "../types/tasks";
import {
  getTasks,
  extractTasksFromResponse,
  updateTask,
  deleteTask,
  getTaskHistory,
  getTaskStatuses,
  createTaskStatus,
  updateTaskStatus,
  deleteTaskStatus,
  cleanupTempTaskAttachments,
  toggleTaskCompleted,
  getTaskSummary,
  moveTask,
  reorderTaskColumns,
  moveAndDeleteTaskStatus,
  getTaskById,
} from "../services/tasks";
import {
  createTaskProject,
  updateTaskProject,
} from "../services/taskProjects";
import { useTaskProjects } from "../hooks/tasks/useTaskProjects";
import { useTaskScope } from "../hooks/tasks/useTaskScope";
import { getUsers } from "../services/users";
import { useUser } from "contexts/UserContext";
import { useResourceSubscription } from "../contexts/NotificationsContext";
import { getContacts } from "../services/contacts";
import "../components/tasks/tasks-responsive.css";

type ViewMode = "list" | "kanban";

export default function TaskPageNew() {
  const { user: currentUser } = useUser();
  const location = useLocation();
  const [view, setView] = useState<ViewMode>(() => window.matchMedia("(max-width: 767px)").matches ? "list" : "kanban");
  const [highlightedTaskId, setHighlightedTaskId] = useState<number | null>(null);
  // FE-7: task completo da aprire dopo aver caricato lo scope corretto (da notifica/ricerca)
  const [pendingTaskToOpen, setPendingTaskToOpen] = useState<Task | null>(null);

  // Rimuovi l'highlight al primo click successivo dell'utente
  useEffect(() => {
    if (highlightedTaskId === null) return;

    const clearHighlight = () => setHighlightedTaskId(null);
    // Piccolo delay per non catturare il click di navigazione che ha portato qui
    const timer = setTimeout(() => {
      document.addEventListener("click", clearHighlight, { once: true });
    }, 500);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", clearHighlight);
    };
  }, [highlightedTaskId]);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [statusColumns, setStatusColumns] = useState<TaskStatusDetail[]>([]);
  // Shape: { [statusId]: { tasks: Task[], count: number, hasMore: boolean, loading: boolean } }
  // Stato SEPARATO dal `tasks` (che serve la vista Lista). Il Kanban consuma solo questo.
  const [tasksByColumn, setTasksByColumn] = useState<Record<number, { tasks: Task[]; count: number; hasMore: boolean; loading: boolean }>>({});
  // Numero progressivo per colonna: impedisce a una risposta HTTP obsoleta di
  // sovrascrivere quella avviata dopo un cambio del toggle.
  const columnRequestVersionRef = useRef<Record<number, number>>({});
  const [users, setUsers] = useState<TaskAssignee[]>([]);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [defaultStatus, setDefaultStatus] = useState<string | undefined>(undefined);
  const [availableContacts, setAvailableContacts] = useState<{ id: string; name: string; type: "person" | "company" }[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters (client-side) - multi-select arrays
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPriority, setFilterPriority] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterAssignedTo, setFilterAssignedTo] = useState<string[]>([]);
  const [filterAssignedBy, setFilterAssignedBy] = useState<string[]>([]);
  const [filterDueDate, setFilterDueDate] = useState<string[]>([]);
  // Toggle per nascondere i task completati
  const [hideCompleted, setHideCompleted] = useState(false);

  // Filter dropdown state
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  const isManager = currentUser?.role === "manager" || currentUser?.is_superuser === true;

  // --- FE-4: scope progetti + integrazione selettore / manager / form ---
  // Lista progetti attivi e archiviati (entrambi necessari per il manager sheet).
  const activeProjectsHook = useTaskProjects({ archived: false });
  const archivedProjectsHook = useTaskProjects({ archived: true });

  // Scope: project | inbox. Fonte di verità per filtrare la board.
  // Si appoggia a useTaskScope che gestisce URL + localStorage (FE-2).
  const { scope, setScope } = useTaskScope({
    activeProjects: activeProjectsHook.projects,
    userId: currentUser?.id,
  });

  // FE-7: Deep-link per task da notifica/ricerca
  // Leggi l'eventuale highlightTaskId o openDetailId dalla navigazione (es. click su notifica)
  // Chiama getTaskById per determinare lo scope corretto prima di aprire il task
  useEffect(() => {
    const state = location.state as { highlightTaskId?: number; openDetailId?: number } | null;
    const taskId = state?.highlightTaskId || state?.openDetailId || null;
    if (taskId) {
      // Pulisci lo state dalla history per evitare re-trigger su refresh/back
      window.history.replaceState({}, "");
      // Chiamiamo getTaskById per ottenere project_detail e determinare lo scope
      getTaskById(taskId).then((response) => {
        const task = response.data;
        if (task) {
          // Deriva lo scope da project_detail
          if (task.project_detail?.id) {
            // Task di progetto: imposta lo scope progetto
            setScope({ kind: "project", projectId: task.project_detail.id });
          } else if (task.project === null) {
            // Task inbox: imposta lo scope inbox
            setScope({ kind: "inbox" });
          }
          // Imposta highlightedTaskId per l'evidenziazione visiva
          if (state?.highlightTaskId) {
            setHighlightedTaskId(state.highlightTaskId);
          }
          // Salva il task completo da aprire dopo il caricamento dello scope
          setPendingTaskToOpen(task);
        }
      }).catch((error) => {
        console.error("Errore caricamento task per deep-link:", error);
        // Fallback: usa highlightedTaskId come prima
        if (state?.highlightTaskId) {
          setHighlightedTaskId(state.highlightTaskId);
        }
      });
    }
  }, [location.state, setScope]);

  // UI: sheet e dialog progetti
  const [projectSheetOpen, setProjectSheetOpen] = useState(false);
  const [projectFormOpen, setProjectFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<TaskProject | null>(null);

  // Prefill per NewTaskModal: progetto/colonna iniziali quando si crea un nuovo task.
  // `undefined` = il modal decide da solo (default inbox se nessun currentTask).
  const [initialProjectId, setInitialProjectId] = useState<number | null | undefined>(undefined);
  const [initialStatusId, setInitialStatusId] = useState<number | null | undefined>(undefined);

  // FE-5 §6.4: state per il MoveTaskDialog (alternativa accessibile al drag).
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveDialogMode, setMoveDialogMode] = useState<MoveTaskMode>("project");
  const [moveDialogTask, setMoveDialogTask] = useState<Task | null>(null);

  // Summary server-side per lo scope corrente (FE plan sezione 7: "Non calcolare
  // più le statistiche dai task caricati. La barra usa sempre TaskSummary del backend").
  const [scopeSummary, setScopeSummary] = useState<{
    total: number;
    completed: number;
    overdue: number;
    priority: { high: number; medium: number; low: number };
  } | null>(null);
  const [scopeSummaryLoading, setScopeSummaryLoading] = useState(false);

  // Inbox count (per badge nel ProjectSelector). Derivato dal summary inbox.
  const [inboxCount, setInboxCount] = useState<number | undefined>(undefined);

  // Fetch initial data
  useEffect(() => {
    fetchAssignableUsers();
    fetchContactsForTask();
    // Le colonne vengono caricate in base allo scope (useEffect sotto).
  }, []);

  // FE-4: quando lo scope cambia (o al primo caricamento), carica:
  //  - le colonne del progetto corrente (se scope progetto)
  //  - i task della lista (project-scoped o inbox)
  //  - summary server-side
  useEffect(() => {
    if (!scope) return;
    // Carica le colonne del progetto (in inbox nessuna colonna).
    if (scope.kind === "project") {
      getTaskStatuses(scope.projectId)
        .then((response) => {
          if (response.data && Array.isArray(response.data)) {
            // Fix colonne: valida il payload al boundary API. Un cast
            // `as TaskStatusDetail[]` non aggiunge i campi mancanti a runtime:
            // se il BE omette `key`, ogni colonna risulta `undefined === undefined`
            // nei confronti UI (menu multipli, DnD rotto). Rifiutiamo esplicitamente.
            const invalidColumn = response.data.find(
              (column: unknown) => !isValidTaskStatusDetail(column)
            );
            if (invalidColumn) {
              throw new Error("Payload colonne non valido: id/key mancanti");
            }
            const activeStatuses = (response.data as TaskStatusDetail[])
              .filter((status) => status && status.is_active)
              .sort((a, b) => (a.order || 0) - (b.order || 0));
            setStatusColumns(activeStatuses);
          }
        })
        .catch((error) => {
          console.error("Errore caricamento colonne del progetto:", error);
          setStatusColumns([]);
        });
    } else {
      // Inbox: nessuna colonna, pulizia.
      setStatusColumns([]);
    }
    // Carica i task della lista per lo scope corrente.
    fetchTasksForScope(scope);
    // Carica summary server-side.
    fetchScopeSummary(scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // FE-7: quando lo scope è caricato e c'è un task pending da aprire (deep-link),
  // apri il side panel con il task caricato.
  useEffect(() => {
    if (!pendingTaskToOpen || !scope) return;

    // Verifica che lo scope corrente corrisponda al task
    const taskScopeMatches =
      (scope.kind === "project" && pendingTaskToOpen.project_detail?.id === scope.projectId) ||
      (scope.kind === "inbox" && pendingTaskToOpen.project === null);

    if (taskScopeMatches) {
      // Apri il side panel con il task
      setCurrentTask(pendingTaskToOpen);
      setSessionKey(null);
      setIsNewTaskModalOpen(true);
      // Reset pending
      setPendingTaskToOpen(null);
    }
  }, [scope, pendingTaskToOpen]);

  // Inbox count: caricato una volta all'avvio per il badge nel ProjectSelector.
  useEffect(() => {
    getTaskSummary({ withoutProject: true })
      .then((data: any) => {
        setInboxCount(typeof data?.total === "number" ? data.total : undefined);
      })
      .catch((err) => {
        // Non fatale.
        console.warn("Errore inbox summary:", err);
      });
  }, []);

  /**
   * FE-4: carica i task della vista Lista per lo scope corrente (FE plan sezione 7).
   * Sostituisce il vecchio fetchAllTasks() senza filtro, mantenendo lo stesso
   * comportamento per i consumer della vista Lista.
   */
  const fetchTasksForScope = useCallback(
    async (currentScope: TaskScope) => {
      setLoading(true);
      try {
        const params: any = { limit: 100 };
        if (currentScope.kind === "project") {
          params.project = currentScope.projectId;
        } else {
          params.without_project = true;
        }
        const response = await getTasks(params);
        const { tasks: rawTasks } = extractTasksFromResponse(response.data);
        const tasksWithDetails = rawTasks.map((task: any) => ({
          ...task,
          priority: PRIORITY_DISPLAY_MAP[task.priority] || task.priority,
          status: task.status_detail || task.status,
          assigned_to: task.assigned_to || [],
          attachments: task.attachments || [],
        }));
        setTasks(tasksWithDetails);
        setFilteredTasks(tasksWithDetails);
      } catch (error) {
        console.error("Errore recupero task per scope:", error);
        setTasks([]);
        setFilteredTasks([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * FE-4: summary server-side per lo scope (FE plan sezione 7).
   * Le statistiche non sono mai calcolate dal dataset client paginato.
   */
  const fetchScopeSummary = useCallback(async (currentScope: TaskScope) => {
    setScopeSummaryLoading(true);
    try {
      const data: any =
        currentScope.kind === "project"
          ? await getTaskSummary({ projectId: currentScope.projectId })
          : await getTaskSummary({ withoutProject: true });
      setScopeSummary(data);
    } catch (error) {
      console.warn("Errore caricamento summary scope:", error);
      setScopeSummary(null);
    } finally {
      setScopeSummaryLoading(false);
    }
  }, []);

  // Chiave stabile delle colonne: un semplice riordino visuale non deve
  // riavviare la paginazione.
  const columnIdsKey = useMemo(
    () => statusColumns.map((c) => c.id).sort().join(","),
    [statusColumns]
  );

  // Realtime: l'agent o un altro client ha creato/modificato/cancellato un task →
  // refetch della lista (vista Lista) e di tutte le colonne caricate (vista Kanban).
  // Su 'reconnect' (recupero dopo blip di rete) facciamo lo stesso lavoro.
  useResourceSubscription('task', () => {
    fetchAllTasks();
    statusColumns.forEach((col) => fetchTasksForColumn(col.id));
  }, [statusColumns]);

  // FE-7: subscription per task_status - ricarica le colonne del progetto corrente
  useResourceSubscription('task_status', () => {
    if (scope?.kind === "project") {
      getTaskStatuses(scope.projectId)
        .then((response) => {
          if (response.data && Array.isArray(response.data)) {
            const activeStatuses = response.data
              .filter((status: TaskStatusDetail) => status && status.is_active)
              .sort(
                (a: TaskStatusDetail, b: TaskStatusDetail) =>
                  (a.order || 0) - (b.order || 0)
              );
            setStatusColumns(activeStatuses);
          }
        })
        .catch((error) => {
          console.error("Errore ricarica colonne (task_status):", error);
        });
    }
  }, [scope]);

  // FE-7: subscription per task_project - ricarica elenco progetti e progetto corrente
  useResourceSubscription('task_project', () => {
    // Ricarica entrambe le liste progetti
    activeProjectsHook.refetch();
    archivedProjectsHook.refetch();

    // Se il progetto corrente è stato archiviato, passa al primo attivo o inbox
    if (scope?.kind === "project") {
      getTaskById(scope.projectId)
        .then((response) => {
          const project = response.data;
          // Se il progetto è archiviato o non esiste più, cambia scope
          if (!project || project.is_archived) {
            toast.info("Il progetto corrente è stato archiviato. Sei stato spostato.");
            const remaining = activeProjectsHook.projects.filter(
              (p) => p.id !== scope.projectId
            );
            if (remaining.length > 0) {
              setScope({ kind: "project", projectId: remaining[0].id });
            } else {
              setScope({ kind: "inbox" });
            }
          }
        })
        .catch(() => {
          // Se errore (progetto non trovato), passa a inbox
          toast.info("Il progetto corrente non è più disponibile. Sei stato spostato nell'inbox.");
          setScope({ kind: "inbox" });
        });
    }
  }, [scope, activeProjectsHook, archivedProjectsHook, setScope]);

  // Apply all client-side filters (multi-select)
  useEffect(() => {
    let result = tasks;

    // Filtro ricerca testo
    if (searchTerm.trim()) {
      result = result.filter((task) =>
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro priorità (multi-select)
    if (filterPriority.length > 0) {
      result = result.filter((task) => filterPriority.includes(task.priority));
    }

    // Filtro colonna/stato (multi-select)
    if (filterStatus.length > 0) {
      result = result.filter((task) => task.status?.key && filterStatus.includes(task.status.key));
    }

    // Filtro "Assegnato a" (multi-select)
    if (filterAssignedTo.length > 0) {
      result = result.filter((task) =>
        task.assigned_to?.some((u) => filterAssignedTo.includes(String(u.id)))
      );
    }

    // Filtro "Assegnato da" (multi-select) - filtra su created_by
    if (filterAssignedBy.length > 0) {
      result = result.filter((task) =>
        task.created_by && filterAssignedBy.includes(task.created_by)
      );
    }

    // Filtro data scadenza (multi-select)
    if (filterDueDate.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);

      result = result.filter((task) => {
        if (!task.due_date) return false;
        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);

        return filterDueDate.some((f) => {
          switch (f) {
            case "today":
              return dueDate.getTime() === today.getTime();
            case "week":
              return dueDate >= today && dueDate <= weekEnd;
            case "overdue":
              return dueDate < today && task.status?.key !== "COMPLETATO";
            default:
              return false;
          }
        });
      });
    }

    // Escludi task completati
    if (hideCompleted) {
      result = result.filter((task) => !task.is_completed);
    }

    setFilteredTasks(result);
  }, [searchTerm, tasks, filterPriority, filterStatus, filterAssignedTo, filterAssignedBy, filterDueDate, hideCompleted]);

  const fetchAssignableUsers = async () => {
    try {
      // getUsers returns users filtered by tenant via backend get_queryset
      const response = await getUsers();
      const usersArray = Array.isArray(response.data) ? response.data : [];
      setUsers(usersArray);
    } catch (error) {
      console.error("Errore recupero utenti:", error);
      setUsers([]);
    }
  };

  const fetchContactsForTask = async () => {
    try {
      const response = await getContacts();
      const contactsArray = Array.isArray(response.data) ? response.data : [];
      const formatted = contactsArray.map((c: any) => ({
        id: String(c.id),
        name: c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "N/A",
        type: (c.contact_type === "company" ? "company" : "person") as "person" | "company",
      }));
      setAvailableContacts(formatted);
    } catch (error) {
      console.error("Errore recupero contatti:", error);
      setAvailableContacts([]);
    }
  };

  const fetchTaskStatuses = useCallback(async () => {
    // Filtra sempre per progetto corrente: senza projectId il BE restituirebbe
    // tutte le colonne del tenant (bug: la board mostrava ~20 colonne di tutti
    // i progetti dopo add/rename/update colonna).
    const projectId = scope?.kind === "project" ? scope.projectId : undefined;
    try {
      const response = await getTaskStatuses(projectId);
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Validazione runtime del contratto colonne (id + key obbligatori).
        // Le righe non conformi vengono segnalate invece di essere droppate
        // silenziosamente, così una regressione del BE non produce menu/DnD rotti.
        const invalidColumn = response.data.find(
          (status: unknown) => !isValidTaskStatusDetail(status)
        );
        if (invalidColumn) {
          console.error("Payload colonne non valido: id/key mancanti", invalidColumn);
        }
        const activeStatuses = response.data
          .filter((status: TaskStatusDetail) => isValidTaskStatusDetail(status) && status.is_active)
          .sort((a: TaskStatusDetail, b: TaskStatusDetail) => (a.order || 0) - (b.order || 0));

        if (activeStatuses.length > 0) {
          setStatusColumns(activeStatuses);
        }
      }
    } catch (error) {
      console.error("Errore caricamento stati:", error);
    }
  }, [scope]);

  // Fetch per la VISTA LISTA (dataset "piatto" separato da tasksByColumn).
  // Il backend ha paginazione attiva (LimitOffsetPagination); usiamo limit=100 (max_limit)
  // e gestiamo la shape paginata via extractTasksFromResponse.
  // Known issue: se ci sono >100 task nel tenant, la Lista ne mostra solo i primi 100.
  const fetchAllTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTasks({ limit: 100 });
      const { tasks: rawTasks } = extractTasksFromResponse(response.data);
      const tasksWithDetails = rawTasks.map((task: any) => ({
        ...task,
        priority: PRIORITY_DISPLAY_MAP[task.priority] || task.priority,
        status: task.status_detail || task.status,
        assigned_to: task.assigned_to || [],
        attachments: task.attachments || [],
      }));
      setTasks(tasksWithDetails);
      setFilteredTasks(tasksWithDetails);
    } catch (error) {
      console.error("Errore recupero task:", error);
      setTasks([]);
      setFilteredTasks([]);
      /*
      // OLD FALLBACK CODE - REMOVED (now using centralized mock system)
      const mockTasks: Task[] = [
        {
          id: 1,
          title: "Implementare nuova funzionalità di autenticazione",
          description: "Aggiungere OAuth2 e JWT",
          status: { id: 2, key: "IN_CORSO", name: "In Corso", color: "#2b7fff", order: 20, is_active: true, is_default: false },
          priority: "ALTA",
          due_date: "2026-02-15",
          assigned_to: [{ id: 1, username: "Marco Rossi", email: "m.rossi@example.com" }],
          attachments: [{
            id: 1,
            file: "spec.pdf",
            filename: "spec.pdf",
            uploaded_by: "Marco Rossi",
            uploaded_at: "2026-01-20T10:30:00Z"
          }],
          done: false,
          created_by: "Marco Rossi",
          created_at: "2026-01-20",
          updated_at: "2026-01-25",
        },
        {
          id: 2,
          title: "Refactoring del modulo task",
          description: "Migliorare performance e UI",
          status: { id: 3, key: "REVISIONE", name: "Revisione", color: "#fe9a00", order: 30, is_active: true, is_default: false },
          priority: "MEDIA",
          due_date: "2026-02-10",
          assigned_to: [{ id: 2, username: "Maria Bianchi", email: "m.bianchi@example.com" }],
          attachments: [],
          done: false,
          created_by: "Maria Bianchi",
          created_at: "2026-01-18",
          updated_at: "2026-01-24",
        },
        {
          id: 3,
          title: "Aggiornare documentazione API",
          description: "Documentare nuovi endpoint",
          status: { id: 4, key: "COMPLETATO", name: "Completato", color: "#00d492", order: 40, is_active: true, is_default: false },
          priority: "BASSA",
          due_date: "2026-01-30",
          assigned_to: [{ id: 3, username: "Sara Verdi", email: "s.verdi@example.com" }],
          attachments: [
            {
              id: 2,
              file: "doc1.pdf",
              filename: "doc1.pdf",
              uploaded_by: "Sara Verdi",
              uploaded_at: "2026-01-15T14:20:00Z"
            },
            {
              id: 3,
              file: "doc2.pdf",
              filename: "doc2.pdf",
              uploaded_by: "Sara Verdi",
              uploaded_at: "2026-01-15T14:25:00Z"
            }
          ],
          done: true,
          created_by: "Sara Verdi",
          created_at: "2026-01-15",
          updated_at: "2026-01-28",
        },
        {
          id: 4,
          title: "Ottimizzare query database",
          description: "Ridurre tempi di risposta",
          status: { id: 1, key: "DA_FARE", name: "Da Fare", color: "#2b7fff", order: 10, is_active: true, is_default: true },
          priority: "ALTA",
          due_date: "2026-02-20",
          assigned_to: [{ id: 1, username: "Marco Rossi", email: "m.rossi@example.com" }],
          attachments: [],
          done: false,
          created_by: "Marco Rossi",
          created_at: "2026-01-22",
          updated_at: "2026-01-22",
        },
        {
          id: 5,
          title: "Implementare sistema di notifiche push",
          description: "Notifiche real-time per gli utenti",
          status: { id: 2, key: "IN_CORSO", name: "In Corso", color: "#2b7fff", order: 20, is_active: true, is_default: false },
          priority: "MEDIA",
          due_date: "2026-03-01",
          assigned_to: [
            { id: 2, username: "Maria Bianchi", email: "m.bianchi@example.com" },
            { id: 3, username: "Sara Verdi", email: "s.verdi@example.com" }
          ],
          attachments: [{
            id: 4,
            file: "wireframe.png",
            filename: "wireframe.png",
            uploaded_by: "Maria Bianchi",
            uploaded_at: "2026-01-20T09:15:00Z"
          }],
          done: false,
          created_by: "Maria Bianchi",
          created_at: "2026-01-20",
          updated_at: "2026-01-26",
        },
      ];
      setTasks(mockTasks);
      setFilteredTasks(mockTasks);
      */
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch paginato di una singola colonna (Kanban). Usato sia al mount sia dal pulsante "Carica altri".
  const fetchTasksForColumn = useCallback(async (statusId: number, opts?: { append?: boolean }) => {
    const append = opts?.append === true;
    const requestVersion = (columnRequestVersionRef.current[statusId] ?? 0) + 1;
    columnRequestVersionRef.current[statusId] = requestVersion;
    setTasksByColumn((prev) => {
      const current = prev[statusId] || { tasks: [], count: 0, hasMore: false, loading: false };
      return { ...prev, [statusId]: { ...current, loading: true } };
    });
    try {
      // Leggo l'offset dallo snapshot corrente (evito la closure stale).
      const currentSnapshot = (prevAll: typeof tasksByColumn) => prevAll[statusId]?.tasks.length ?? 0;
      // Offset calcolato al momento della chiamata (non dentro setState) per coerenza con la API.
      const offset = append
        ? (tasksByColumn[statusId]?.tasks.length ?? 0)
        : 0;
      // FIX BUG-2: Passa project parameter come richiesto dalla documentazione
      // Se scope è inbox, passa without_project=true. Se project, passa project ID.
      const params: any = {
        status: statusId,
        limit: 10,
        offset,
        // Il backend deve filtrare prima di applicare limit/offset. Quando il
        // toggle è spento il parametro viene omesso e la board resta completa.
        is_completed: hideCompleted ? false : undefined,
      };
      if (scope?.kind === "project") {
        params.project = scope.projectId;
      }
      const response = await getTasks(params);
      const { tasks: rawTasks, count, hasMore } = extractTasksFromResponse(response.data);
      const mapped = rawTasks.map((task: any) => ({
        ...task,
        priority: PRIORITY_DISPLAY_MAP[task.priority] || task.priority,
        status: task.status_detail || task.status,
        assigned_to: task.assigned_to || [],
        attachments: task.attachments || [],
      }));
      setTasksByColumn((prev) => {
        if (columnRequestVersionRef.current[statusId] !== requestVersion) return prev;
        const current = prev[statusId] || { tasks: [], count: 0, hasMore: false, loading: false };
        return {
          ...prev,
          [statusId]: {
            tasks: append ? [...current.tasks, ...mapped] : mapped,
            count,
            hasMore,
            loading: false,
          },
        };
      });
      // Silence unused capture
      void currentSnapshot;
    } catch (error) {
      console.error("Errore caricamento task colonna:", error);
      setTasksByColumn((prev) => {
        if (columnRequestVersionRef.current[statusId] !== requestVersion) return prev;
        const current = prev[statusId] || { tasks: [], count: 0, hasMore: false, loading: false };
        return { ...prev, [statusId]: { ...current, loading: false } };
      });
    }
  }, [tasksByColumn, scope, hideCompleted]);

  // Riparte sempre da offset zero quando cambiano progetto, colonne o toggle.
  // Così la pagina contiene fino a 10 task che rispettano davvero il filtro.
  useEffect(() => {
    if (statusColumns.length === 0) return;
    statusColumns.forEach((col) => {
      fetchTasksForColumn(col.id);
    });
    // La callback dipende dal dataset corrente per l'offset di "Carica altri";
    // includerla causerebbe un refetch dopo ogni risposta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnIdsKey, scope?.kind, scope?.kind === "project" ? scope.projectId : null, hideCompleted]);

  const loadMoreTasks = useCallback((statusId: number) => {
    return fetchTasksForColumn(statusId, { append: true });
  }, [fetchTasksForColumn]);

  const fetchTaskHistory = async (taskId: number) => {
    try {
      const response = await getTaskHistory(taskId);
      return response.data;
    } catch (error) {
      console.error("Errore recupero cronologia:", error);
      return [];
    }
  };

  const handleOpenTask = useCallback((taskId: number) => {
    const task = tasks.find((t) => t.id === taskId);
    if (task) {
      setCurrentTask(task);
      setSessionKey(null);
      setIsNewTaskModalOpen(true);
    }
  }, [tasks]);

  const handleCreateTask = useCallback((status: string) => {
    setCurrentTask(null);
    setDefaultStatus(status);
    setSessionKey(generateSessionKey());
    // Prefill dallo scope corrente (FE-4 §6.5).
    if (scope?.kind === "project") {
      // Cerca lo statusId dalla colonna con key === status nello scope corrente.
      const col = statusColumns.find((c) => c.key === status);
      setInitialProjectId(scope.projectId);
      setInitialStatusId(col?.id ?? null);
    } else if (scope?.kind === "inbox") {
      setInitialProjectId(null);
      setInitialStatusId(null);
    } else {
      setInitialProjectId(undefined);
      setInitialStatusId(undefined);
    }
    setIsNewTaskModalOpen(true);
  }, [scope, statusColumns]);

  /**
   * Apri il modal "Nuovo task" prefilled per la board corrente (FE-4 §6.5).
   * Usato dal pulsante "Nuovo Task" generale: usa scope corrente.
   */
  const handleCreateTaskFromHeader = useCallback(() => {
    setCurrentTask(null);
    setDefaultStatus(undefined);
    setSessionKey(generateSessionKey());
    if (scope?.kind === "project") {
      setInitialProjectId(scope.projectId);
      // Default: prima colonna del progetto (o null se non ce ne sono).
      setInitialStatusId(statusColumns[0]?.id ?? null);
    } else if (scope?.kind === "inbox") {
      setInitialProjectId(null);
      setInitialStatusId(null);
    } else {
      setInitialProjectId(undefined);
      setInitialStatusId(undefined);
    }
    setIsNewTaskModalOpen(true);
  }, [scope, statusColumns]);

  /**
   * Apri il modal in modalità "assegna a progetto" per un task inbox (FE-4 §6.5).
   * Ricarica il task completo (per avere project/status attuali), poi apre il
   * modal in edit con initialProjectId=null (forzando l'apertura sulla sezione
   * progetto/colonna per consentire l'assegnazione).
   */
  const handleAssignToProject = useCallback(
    async (taskId: number) => {
      // Provo a usare il task in stato locale; se non c'è (es. task caricato
      // solo nella lista) lo cerco su tutti gli array noti.
      const local = tasks.find((t) => t.id === taskId) || null;
      setCurrentTask(local);
      setSessionKey(null);
      setInitialProjectId(local?.project ?? null);
      setInitialStatusId(local?.status?.id ?? null);
      setDefaultStatus(undefined);
      setIsNewTaskModalOpen(true);
    },
    [tasks]
  );

  const handleTaskSaved = (savedTask: any) => {
    // Il task è già stato creato/aggiornato da NewTaskModal, aggiorna solo lo state locale
    const mappedTask: Task = {
      ...savedTask,
      priority: PRIORITY_DISPLAY_MAP[savedTask.priority] || savedTask.priority,
      status: savedTask.status_detail || savedTask.status,
      assigned_to: savedTask.assigned_to || [],
      attachments: savedTask.attachments || [],
    };

    const isUpdate = savedTask.id && tasks.some((t) => t.id === savedTask.id);
    if (isUpdate) {
      // Update existing task
      setTasks(tasks.map((t) => (t.id === savedTask.id ? mappedTask : t)));
      setFilteredTasks(filteredTasks.map((t) => (t.id === savedTask.id ? mappedTask : t)));
    } else {
      // Create new task
      setTasks([...tasks, mappedTask]);
      setFilteredTasks([...filteredTasks, mappedTask]);
    }

    // Aggiorna tasksByColumn (Kanban).
    const newStatusId = mappedTask.status?.id;
    if (typeof newStatusId === "number") {
      setTasksByColumn((prev) => {
        const next = { ...prev };
        let wasInNewCol = false;
        if (isUpdate) {
          for (const [sid, st] of Object.entries(prev)) {
            const sidNum = Number(sid);
            const inCol = st.tasks.some((t) => t.id === mappedTask.id);
            if (!inCol) continue;
            if (sidNum === newStatusId) {
              // Update in place, nessun change di count
              next[sidNum] = {
                ...st,
                tasks: st.tasks.map((t) => (t.id === mappedTask.id ? mappedTask : t)),
              };
              wasInNewCol = true;
            } else {
              // Rimuovi dalla vecchia colonna
              next[sidNum] = {
                ...st,
                tasks: st.tasks.filter((t) => t.id !== mappedTask.id),
                count: Math.max(0, st.count - 1),
              };
            }
          }
        }
        // Se il task non era già nella colonna target, aggiungilo e incrementa il count.
        if (!wasInNewCol) {
          const targetCol = next[newStatusId];
          if (targetCol) {
            next[newStatusId] = {
              ...targetCol,
              tasks: [mappedTask, ...targetCol.tasks],
              count: targetCol.count + 1,
            };
          }
        }
        return next;
      });
    }

    if (sessionKey) {
      setSessionKey(null);
    }
  };

  /**
   * FE-5: spostamento atomico di un task via `moveTask` action (BE 6.3).
   * Una sola chiamata POST /tasks/{id}/move/ con {project, status, position}.
   * Ottimistico su tasks/tasksByColumn; su errore REJECT così il Kanban può
   * ripristinare lo snapshot visuale. Refetch delle sole colonne coinvolte.
   *
   * NB: il Kanban esiste solo nello scope progetto (inbox non ha Kanban),
   * quindi `scope.projectId` è sempre valorizzato quando questa funzione
   * viene chiamata dal drag.
   */
  const handleTaskMove = useCallback(
    async (taskId: number, toStatusId: number, toPosition: number, fromStatusId: number) => {
      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;

      const targetStatus = statusColumns.find((s) => s.id === toStatusId);
      if (!targetStatus) return;

      const projectId =
        scope?.kind === "project" ? scope.projectId : task.project ?? null;

      // Optimistic update vista Lista (stato `tasks`)
      const updatedTasks = tasks.map((t) =>
        t.id === taskId ? { ...t, status: targetStatus } : t
      );
      setTasks(updatedTasks);
      setFilteredTasks(updatedTasks);

      // Optimistic update Kanban (tasksByColumn): sposta il task e aggiorna i count.
      // `fromStatusId` arriva dal chiamante (handleDragEnd del Kanban), che lo risolve
      // dalla key originale salvata in handleDragStart — NON dal task, che in handleDragOver
      // è stato mutato e riporterebbe già il nuovo status.
      if (fromStatusId !== toStatusId) {
        setTasksByColumn((prev) => {
          const next = { ...prev };
          const src = prev[fromStatusId];
          if (src) {
            next[fromStatusId] = {
              ...src,
              tasks: src.tasks.filter((t) => t.id !== taskId),
              count: Math.max(0, src.count - 1),
            };
          }
          const dst = prev[toStatusId];
          const movedTask = { ...task, status: targetStatus } as Task;
          if (dst) {
            // Inserisci alla posizione indicata (best-effort: fall-back su prepend).
            const without = dst.tasks.filter((t) => t.id !== taskId);
            const insertAt = Math.max(0, Math.min(toPosition, without.length));
            without.splice(insertAt, 0, movedTask);
            next[toStatusId] = {
              ...dst,
              tasks: without,
              count: dst.count + 1,
            };
          }
          return next;
        });
      }

      try {
        // Singola action atomica (BE 6.3). `position` inoltrato per riordino same/colonna.
        await moveTask(taskId, {
          project: projectId,
          status: toStatusId,
          position: toPosition,
        });
        // Aggiorna summary server-side (i count potrebbero essere cambiati).
        if (scope) fetchScopeSummary(scope);
      } catch (error) {
        console.error("Spostamento atomico fallito:", error);
        // Refetch delle sole colonne coinvolte per riallineare con il BE.
        await Promise.all([
          fetchTasksForColumn(fromStatusId),
          fetchTasksForColumn(toStatusId),
          fetchAllTasks(),
        ]);
        // Rifiuta per permettere al Kanban di ripristinare lo snapshot visuale.
        throw error;
      }
    },
    [tasks, statusColumns, scope, fetchAllTasks, fetchTasksForColumn, fetchScopeSummary]
  );

  /**
   * FE-5 §6.4: submit del MoveTaskDialog. Sposta il task via moveTask atomica.
   * Aggiorna stato locale (tasks/tasksByColumn), mostra toast di successo
   * e rigetta su errore per mostrare il messaggio inline nel dialog.
   */
  const handleMoveTaskSubmit = useCallback(
    async (payload: MoveTaskPayload) => {
      if (!moveDialogTask?.id) return;
      const taskId = moveDialogTask.id;

      try {
        const result = await moveTask(taskId, {
          project: payload.project,
          status: payload.status,
          position: 0,
        });
        // Aggiorna lo stato locale con la response autoritativa.
        const mappedTask: Task = {
          ...(result as any),
          priority: PRIORITY_DISPLAY_MAP[(result as any)?.priority] || (result as any)?.priority,
          status: (result as any)?.status_detail || (result as any)?.status || null,
          assigned_to: (result as any)?.assigned_to || [],
          attachments: (result as any)?.attachments || [],
        };
        setTasks((prev) => {
          const exists = prev.some((t) => t.id === taskId);
          return exists
            ? prev.map((t) => (t.id === taskId ? mappedTask : t))
            : [mappedTask, ...prev];
        });
        setFilteredTasks((prev) => {
          const exists = prev.some((t) => t.id === taskId);
          // Se il task è stato spostato fuori dallo scope corrente (altro progetto),
          // rimuovilo dalla vista Lista locale.
          const outOfScope =
            scope?.kind === "project" && payload.project !== scope.projectId;
          if (outOfScope) return prev.filter((t) => t.id !== taskId);
          if (scope?.kind === "inbox" && payload.project !== null) {
            return prev.filter((t) => t.id !== taskId);
          }
          return exists
            ? prev.map((t) => (t.id === taskId ? mappedTask : t))
            : [mappedTask, ...prev];
        });
        setTasksByColumn((prev) => {
          const next: typeof prev = {};
          for (const [sid, st] of Object.entries(prev)) {
            const sidNum = Number(sid);
            const had = st.tasks.some((t) => t.id === taskId);
            if (payload.project === null || payload.status == null) {
              // Move to inbox: nessuna colonna lo contiene.
              next[sidNum] = had
                ? { ...st, tasks: st.tasks.filter((t) => t.id !== taskId), count: Math.max(0, st.count - 1) }
                : st;
            } else if (payload.status === sidNum) {
              // Aggiungi alla colonna target.
              const without = st.tasks.filter((t) => t.id !== taskId);
              next[sidNum] = {
                ...st,
                tasks: [mappedTask, ...without],
                count: had ? st.count : st.count + 1,
              };
            } else if (had) {
              // Rimuovi dalla vecchia colonna.
              next[sidNum] = {
                ...st,
                tasks: st.tasks.filter((t) => t.id !== taskId),
                count: Math.max(0, st.count - 1),
              };
            } else {
              next[sidNum] = st;
            }
          }
          return next;
        });
        // Aggiorna summary (count potrebbero essere cambiati).
        if (scope) fetchScopeSummary(scope);
        const destName =
          payload.project === null
            ? "inbox"
            : activeProjectsHook.projects.find((p) => p.id === payload.project)?.name ?? "progetto";
        toast.success(`Task spostato in "${destName}".`);
      } catch (err) {
        // Rigetta per mostrare l'errore inline nel dialog (describeMoveError).
        throw err;
      }
    },
    [moveDialogTask, scope, activeProjectsHook.projects, fetchScopeSummary]
  );

  /**
   * Apre MoveTaskDialog mode="column" per spostare il task in un'altra colonna
   * dello stesso progetto (FE-5 §8). Usata dalla card menu del Kanban e ListView.
   */
  const handleMoveTaskToColumn = useCallback(
    (taskId: number) => {
      const task = tasks.find((t) => t.id === taskId) || null;
      setMoveDialogTask(task);
      setMoveDialogMode("column");
      setMoveDialogOpen(true);
    },
    [tasks]
  );

  /**
   * Apre MoveTaskDialog mode="project" per spostare il task in un altro progetto
   * (o inbox) — FE-5 §8.
   */
  const handleMoveTaskToProject = useCallback(
    (taskId: number) => {
      const task = tasks.find((t) => t.id === taskId) || null;
      setMoveDialogTask(task);
      setMoveDialogMode("project");
      setMoveDialogOpen(true);
    },
    [tasks]
  );

  /**
   * FE-5 §8: riordino colonne batch via singola action `reorderTaskColumns`.
   * Salva ordini 10,20,30,... transazionalmente. Su errore REJECT per
   * permettere al Kanban di ripristinare lo snapshot ordine precedente.
   */
  const handleColumnOrderChange = useCallback(
    async (newOrder: TaskStatusDetail[]) => {
      const projectId = scope?.kind === "project" ? scope.projectId : null;
      if (projectId == null) {
        // Senza progetto non dovremmo mai essere qui (nessun Kanban in inbox).
        return;
      }
      const columnIds = newOrder.map((c) => c.id);
      try {
        await reorderTaskColumns(projectId, columnIds);
        // Salva localmente i nuovi ordini per coerenza visuale e filtri.
        setStatusColumns(
          newOrder.map((c, idx) => ({ ...c, order: (idx + 1) * 10 }))
        );
        toast.success("Ordine colonne aggiornato.");
      } catch (error) {
        console.error("Riordino colonne fallito:", error);
        // Ripristina l'ordine precedente dal BE.
        try {
          await fetchTaskStatuses();
        } catch (e) {
          console.error("Refetch colonne fallito:", e);
        }
        throw error;
      }
    },
    [scope, fetchTaskStatuses]
  );

  const handleRenameStatus = useCallback(async (statusId: number, newName: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // La `key` è gestita server-side: il FE invia solo il nuovo nome.
      await updateTaskStatus(statusId, { name: newName });
      await fetchTaskStatuses();
      return { success: true };
    } catch (error: any) {
      console.error("Errore rinomina colonna:", error);
      let errorMessage = "Errore durante la rinomina della colonna";
      const data = error.response?.data;
      if (data) {
        if (Array.isArray(data.name) && data.name.length) {
          errorMessage = String(data.name[0]);
        } else if (Array.isArray(data.key) && data.key.length) {
          errorMessage = String(data.key[0]);
        } else if (data.non_field_errors) {
          errorMessage = "Esiste già una colonna con questo nome";
        } else if (data.detail) {
          errorMessage = String(data.detail);
        }
      }
      return { success: false, error: errorMessage };
    }
  }, [fetchTaskStatuses]);

  /**
   * Aggiorna nome e colore di una colonna.
   */
  const handleUpdateColumn = useCallback(async (statusId: number, name: string, color: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await updateTaskStatus(statusId, { name, color });
      await fetchTaskStatuses();
      toast.success("Colonna aggiornata.");
      return { success: true };
    } catch (error: any) {
      console.error("Errore aggiornamento colonna:", error);
      let errorMessage = "Errore durante l'aggiornamento della colonna";
      const data = error.response?.data;
      if (data) {
        if (Array.isArray(data.name) && data.name.length) {
          errorMessage = String(data.name[0]);
        } else if (Array.isArray(data.color) && data.color.length) {
          errorMessage = String(data.color[0]);
        } else if (Array.isArray(data.key) && data.key.length) {
          errorMessage = String(data.key[0]);
        } else if (data.non_field_errors) {
          errorMessage = "Esiste già una colonna con questo nome";
        } else if (data.detail) {
          errorMessage = String(data.detail);
        }
      }
      return { success: false, error: errorMessage };
    }
  }, [fetchTaskStatuses]);

  /**
   * FE-6: elimina una colonna VUOTA con DELETE semplice.
   * Il parametro `force` è stato rimosso dal contratto BE.
   */
  const handleDeleteEmptyColumn = useCallback(async (statusId: number): Promise<{ success: boolean; error?: string }> => {
    try {
      await deleteTaskStatus(statusId);
      setStatusColumns((prev) => prev.filter((c) => c.id !== statusId));
      setTasksByColumn((prev) => {
        const next = { ...prev };
        delete next[statusId];
        return next;
      });
      toast.success("Colonna eliminata.");
      return { success: true };
    } catch (error: any) {
      console.error("Errore eliminazione colonna vuota:", error);
      const data = error.response?.data;
      let errorMessage = "Errore durante l'eliminazione della colonna";
      if (data?.detail) {
        errorMessage = String(data.detail);
      }
      return { success: false, error: errorMessage };
    }
  }, []);

  /**
   * FE-6: sposta i task in un'altra colonna e elimina la colonna (move-and-delete).
   * Usato quando la colonna contiene task. Operazione atomica BE 6.2.
   */
  const handleMoveAndDeleteColumn = useCallback(async (statusId: number, destinationId: number): Promise<{ success: boolean; error?: string }> => {
    try {
      // Chiamata atomica move-and-delete
      await moveAndDeleteTaskStatus(statusId, destinationId);

      // Rimuovi la colonna eliminata
      setStatusColumns((prev) => prev.filter((c) => c.id !== statusId));
      setTasksByColumn((prev) => {
        const next = { ...prev };
        // Rimuovi la colonna sorgente
        delete next[statusId];
        // I task sono stati spostati nel backend, refetch la colonna destinazione
        if (next[destinationId]) {
          // Non facciamo update ottimistico: refetchiamo per allinearci con il BE
          fetchTasksForColumn(destinationId);
        }
        return next;
      });

      // Refetch lista task per allineare
      fetchAllTasks();
      // Aggiorna summary
      if (scope) fetchScopeSummary(scope);

      toast.success("Colonna eliminata e task spostati.");
      return { success: true };
    } catch (error: any) {
      console.error("Errore move-and-delete colonna:", error);
      const data = error.response?.data;
      let errorMessage = "Non è stato possibile eliminare la colonna. Riprova.";
      if (data?.detail) {
        errorMessage = String(data.detail);
      } else if (data?.code) {
        errorMessage = `Errore: ${data.code}`;
      }
      return { success: false, error: errorMessage };
    }
  }, [scope, fetchScopeSummary, fetchAllTasks]);

  /**
   * FE-6: crea una nuova colonna inviando sempre `project` nel payload.
   * Solo nello scope progetto è possibile creare colonne.
   */
  const handleAddColumn = useCallback(async (): Promise<{ success: boolean; newColumnId?: number; error?: string }> => {
    const projectId = scope?.kind === "project" ? scope.projectId : null;
    if (projectId == null) {
      return { success: false, error: "Impossibile creare colonne fuori da un progetto" };
    }

    try {
      const maxOrder = Math.max(...statusColumns.map(s => s.order || 0), 0);
      const newOrder = maxOrder + 10;

      // La `key` è generata server-side (slugify del name): il FE non la invia.
      // FE-6: inviare sempre `project` nel payload (BE 6.2).
      const response = await createTaskStatus({
        name: "Nuova colonna",
        color: "#7b3fff",
        order: newOrder,
        is_active: true,
        is_default: false,
        project: projectId,
      });

      await fetchTaskStatuses();
      toast.success("Colonna creata.");
      return { success: true, newColumnId: response.data?.id };
    } catch (error: any) {
      console.error("Errore creazione colonna:", error);
      let errorMessage = "Errore durante la creazione della colonna";
      const data = error.response?.data;
      if (data) {
        if (Array.isArray(data.name) && data.name.length) {
          errorMessage = String(data.name[0]);
        } else if (data.detail) {
          errorMessage = String(data.detail);
        }
      }
      return { success: false, error: errorMessage };
    }
  }, [statusColumns, scope, fetchTaskStatuses]);

  const handleDeleteTask = useCallback(async (taskId: number): Promise<void> => {
    try {
      await deleteTask(taskId);
      // Ottimistic update invece di refetch: aggiorna tasks (Lista) e tasksByColumn (Kanban).
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      setFilteredTasks((prev) => prev.filter((t) => t.id !== taskId));
      setTasksByColumn((prev) => {
        const next: typeof prev = {};
        for (const [sid, st] of Object.entries(prev)) {
          const sidNum = Number(sid);
          const had = st.tasks.some((t) => t.id === taskId);
          next[sidNum] = had
            ? { ...st, tasks: st.tasks.filter((t) => t.id !== taskId), count: Math.max(0, st.count - 1) }
            : st;
        }
        return next;
      });
    } catch (error: any) {
      console.error("Errore eliminazione task:", error);
      throw error;
    }
  }, []);

  const handleToggleCompleted = useCallback(async (taskId: number): Promise<void> => {
    // Optimistic update: inverte is_completed nello stato locale senza refetch
    const toggleIn = (arr: Task[]) =>
      arr.map((t) => (t.id === taskId ? { ...t, is_completed: !t.is_completed } : t));
    const setIn = (arr: Task[], val: boolean) =>
      arr.map((t) => (t.id === taskId ? { ...t, is_completed: val } : t));

    setTasks(toggleIn);
    setFilteredTasks(toggleIn);
    setTasksByColumn((prev) => {
      const next: typeof prev = {};
      for (const [sid, st] of Object.entries(prev)) {
        next[Number(sid)] = { ...st, tasks: toggleIn(st.tasks) };
      }
      return next;
    });
    try {
      const result = await toggleTaskCompleted(taskId);
      setTasks((prev) => setIn(prev, result.is_completed));
      setFilteredTasks((prev) => setIn(prev, result.is_completed));
      setTasksByColumn((prev) => {
        const next: typeof prev = {};
        for (const [sid, st] of Object.entries(prev)) {
          next[Number(sid)] = { ...st, tasks: setIn(st.tasks, result.is_completed) };
        }
        return next;
      });
    } catch (error) {
      console.error("Errore toggle completato:", error);
      setTasks(toggleIn);
      setFilteredTasks(toggleIn);
      setTasksByColumn((prev) => {
        const next: typeof prev = {};
        for (const [sid, st] of Object.entries(prev)) {
          next[Number(sid)] = { ...st, tasks: toggleIn(st.tasks) };
        }
        return next;
      });
    }
  }, []);

  const handleModalClose = async () => {
    if (sessionKey) {
      try {
        await cleanupTempTaskAttachments(sessionKey);
      } catch (error) {
        console.error("Errore pulizia allegati temporanei:", error);
      }
    }
    setIsNewTaskModalOpen(false);
    setCurrentTask(null);
    setSessionKey(null);
    setDefaultStatus(undefined);
    setInitialProjectId(undefined);
    setInitialStatusId(undefined);
  };

  // --- FE-4: handler progetto form / manager sheet ---

  const handleOpenNewProject = useCallback(() => {
    setEditingProject(null);
    setProjectFormOpen(true);
  }, []);

  const handleOpenEditProject = useCallback((project: TaskProject) => {
    setEditingProject(project);
    setProjectFormOpen(true);
  }, []);

  const handleOpenManageProjects = useCallback(() => {
    setProjectSheetOpen(true);
  }, []);

  /**
   * Submit del ProjectForm: crea o aggiorna. Mostra toast di successo e
   * ricarica entrambe le liste (attivi + archiviati).
   */
  const handleProjectSubmit = useCallback(
    async (payload: any, isEdit: boolean) => {
      let createdProjectId: number | undefined;
      if (isEdit && editingProject) {
        await updateTaskProject(editingProject.id, payload);
        projectFormToast.updated(editingProject.name);
      } else {
        const created = await createTaskProject(payload);
        createdProjectId = created.data.id;
        projectFormToast.created(created.data.name);
      }
      // Ricarica entrambe le liste (il nuovo progetto va in attivi, l'edit
      // potrebbe aver modificato l'ordine).
      await Promise.all([
        activeProjectsHook.refetch(),
        archivedProjectsHook.refetch(),
      ]);
      // Se è una creazione (non edit), seleziona automaticamente il nuovo progetto
      if (!isEdit && createdProjectId !== undefined) {
        setScope({ kind: "project", projectId: createdProjectId });
      }
      setEditingProject(null);
    },
    [editingProject, activeProjectsHook, archivedProjectsHook, setScope]
  );

  /**
   * Callback del ProjectManagerSheet quando il progetto CORRENTE viene
   * archiviato. Sceglie il primo progetto attivo rimanente o inbox.
   */
  const handleCurrentProjectArchived = useCallback(
    (_archivedId: number) => {
      // refetch per ottenere la lista aggiornata; al refresh il primo progetto
      // attivo sarà disponibile come fallback.
      activeProjectsHook.refetch().then(() => {
        // Lo scope verrà ricalcolato da useTaskScope se scope === null.
        // Forziamo il set esplicito per chiarezza UX.
        const remaining = activeProjectsHook.projects.filter(
          (p) => p.id !== _archivedId
        );
        if (remaining.length > 0) {
          setScope({ kind: "project", projectId: remaining[0].id });
        } else {
          setScope({ kind: "inbox" });
        }
      });
      toast.info("Il progetto corrente è stato archiviato. Sei stato spostato.");
    },
    [activeProjectsHook, setScope]
  );

  // Conteggio filtri attivi (totale valori selezionati)
  const activeFiltersCount =
    filterPriority.length +
    filterStatus.length +
    filterAssignedTo.length +
    filterAssignedBy.length +
    filterDueDate.length;

  const hasActiveFilters = activeFiltersCount > 0;

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterPriority([]);
    setFilterStatus([]);
    setFilterAssignedTo([]);
    setFilterAssignedBy([]);
    setFilterDueDate([]);
    setExpandedFilter(null);
  };

  // Click outside handler for filter dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsFilterDropdownOpen(false);
        setExpandedFilter(null);
      }
    };

    if (isFilterDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterDropdownOpen]);

  // Helper functions for filter display values
  const getPriorityLabel = (value: string) => {
    switch (value) {
      case "ALTA": return "Alta";
      case "MEDIA": return "Media";
      case "BASSA": return "Bassa";
      default: return value;
    }
  };

  const getPriorityColor = (value: string) => {
    switch (value) {
      case "ALTA": return "#ef4444";
      case "MEDIA": return "#f59e0b";
      case "BASSA": return "#22c55e";
      default: return "var(--hexa-purple)";
    }
  };

  const getStatusColor = (key: string) => {
    const status = statusColumns.find(s => s.key === key);
    return status?.color || "var(--hexa-purple)";
  };

  const getStatusLabel = (key: string) => {
    const status = statusColumns.find(s => s.key === key);
    return status?.name || key;
  };

  const getDueDateLabel = (value: string) => {
    switch (value) {
      case "today": return "Oggi";
      case "week": return "Questa settimana";
      case "overdue": return "Scaduti";
      default: return value;
    }
  };

  const getDueDateColor = (value: string) => {
    switch (value) {
      case "today": return "#3b82f6";
      case "week": return "#8b5cf6";
      case "overdue": return "#ef4444";
      default: return "var(--hexa-purple)";
    }
  };

  const getAssigneeLabel = (id: string) => {
    const user = users.find(u => u.id === Number(id));
    return user?.username || id;
  };

  // Toggle filter value (add/remove from array)
  const toggleFilterValue = (
    currentValues: string[],
    setValue: React.Dispatch<React.SetStateAction<string[]>>,
    value: string
  ) => {
    if (currentValues.includes(value)) {
      setValue(currentValues.filter(v => v !== value));
    } else {
      setValue([...currentValues, value]);
    }
  };

  // Get summary text for filter section
  const getFilterSummary = (values: string[], getLabel: (v: string) => string) => {
    if (values.length === 0) return null;
    if (values.length <= 2) {
      return values.map(getLabel).join(", ");
    }
    return `${values.length} selezionati`;
  };

  // Calculate statistics — prefer server-side summary (FE plan sezione 7).
  // Fallback al calcolo client-side solo se scopeSummary non è ancora pronto
  // (es. cambio scope rapido), per evitare un flicker a zero.
  const total = scopeSummary?.total ?? tasks.length;
  const completed =
    scopeSummary?.completed ?? tasks.filter((t) => t.is_completed === true).length;
  const overdue =
    scopeSummary?.overdue ??
    tasks.filter((t) => {
      if (!t.due_date) return false;
      return new Date(t.due_date) < new Date() && t.status?.key !== "COMPLETATO";
    }).length;

  const urgent = scopeSummary?.priority.high ?? tasks.filter((t) => t.priority === "ALTA").length;
  const medium = scopeSummary?.priority.medium ?? tasks.filter((t) => t.priority === "MEDIA").length;
  const low = scopeSummary?.priority.low ?? tasks.filter((t) => t.priority === "BASSA").length;

  // Flat dei task Kanban derivato da tasksByColumn (dataset paginato, max 10 per colonna).
  // NON viene usato per la vista Lista — che ha un fetch separato su `tasks`.
  const kanbanTasksFlat = useMemo(
    () => Object.values(tasksByColumn).flatMap((c) => c.tasks),
    [tasksByColumn]
  );

  // Applica gli stessi filtri client-side al dataset Kanban.
  // NB: known limitation — con paginazione per-colonna i filtri operano solo sui task
  // già caricati; un task non caricato non compare anche se matcha.
  const filteredKanbanTasks = useMemo(() => {
    let result = kanbanTasksFlat;
    if (searchTerm.trim()) {
      result = result.filter((t) =>
        t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterPriority.length > 0) {
      result = result.filter((t) => filterPriority.includes(t.priority));
    }
    if (filterStatus.length > 0) {
      result = result.filter((t) => t.status?.key && filterStatus.includes(t.status.key));
    }
    if (filterAssignedTo.length > 0) {
      result = result.filter((t) => t.assigned_to?.some((u) => filterAssignedTo.includes(String(u.id))));
    }
    if (filterAssignedBy.length > 0) {
      result = result.filter((t) => t.created_by && filterAssignedBy.includes(t.created_by));
    }
    if (filterDueDate.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);
      result = result.filter((task) => {
        if (!task.due_date) return false;
        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);
        return filterDueDate.some((f) => {
          switch (f) {
            case "today": return dueDate.getTime() === today.getTime();
            case "week": return dueDate >= today && dueDate <= weekEnd;
            case "overdue": return dueDate < today && task.status?.key !== "COMPLETATO";
            default: return false;
          }
        });
      });
    }
    // Escludi task completati
    if (hideCompleted) {
      result = result.filter((task) => !task.is_completed);
    }
    return result;
  }, [kanbanTasksFlat, searchTerm, filterPriority, filterStatus, filterAssignedTo, filterAssignedBy, filterDueDate, hideCompleted]);

  // Stati per-colonna passati al TaskKanbanView per header count, hasMore, loading.
  const columnStates = useMemo(() => {
    const obj: Record<number, { count: number; hasMore: boolean; loading: boolean }> = {};
    for (const [sid, st] of Object.entries(tasksByColumn)) {
      obj[Number(sid)] = { count: st.count, hasMore: st.hasMore, loading: st.loading };
    }
    return obj;
  }, [tasksByColumn]);

  return (
    <div className="tasks-page w-full min-w-0 max-w-7xl mx-auto p-3 sm:p-6 pb-28 sm:pb-28 space-y-4">
      {/* Row 1 — Project selector + Manage + Nuovo Task */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-1 min-w-0 flex-wrap items-center gap-2">
          <ProjectSelector
            projects={activeProjectsHook.projects}
            scope={scope}
            inboxCount={inboxCount}
            loading={activeProjectsHook.loading && activeProjectsHook.projects.length === 0}
            onScopeChange={setScope}
            onCreateProject={handleOpenNewProject}
            onManageProjects={handleOpenManageProjects}
          />
          <button
            type="button"
            onClick={handleOpenManageProjects}
            className="inline-flex items-center h-10 px-3 rounded-md border border-[rgba(106,77,255,0.25)] hover:border-[var(--hexa-purple)] text-sm font-medium text-[var(--hexa-text-secondary)] transition"
            title="Gestisci progetti"
          >
            Gestisci progetti
          </button>
        </div>
        <button
          type="button"
          onClick={handleCreateTaskFromHeader}
          className="inline-flex shrink-0 items-center h-10 px-3 rounded-md bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white text-sm font-medium transition"
        >
          <Plus className="h-3.5 w-3.5 mr-0.5" />
          Nuovo Task
        </button>
      </div>

      {/* Row 2 — Search + Filtri + Toggle vista */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-3 flex-1 basis-full sm:basis-auto">
          {/* Search bar */}
          <div className="relative min-w-0 w-full sm:max-w-[320px] sm:flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--hexa-text-muted)]" />
            <Input
              placeholder="Cerca task..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-10 pl-10 pr-10 bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] rounded-lg"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)]"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Filter button with dropdown */}
          <div className="relative shrink-0" ref={filterDropdownRef}>
            <button
              type="button"
              onClick={() => {
                setIsFilterDropdownOpen(!isFilterDropdownOpen);
                if (isFilterDropdownOpen) setExpandedFilter(null);
              }}
              aria-expanded={isFilterDropdownOpen}
              className={`inline-flex h-10 items-center gap-2 rounded-xl bg-[var(--hexa-card-bg)] border px-3 py-1.5 text-sm font-medium transition ${
                hasActiveFilters
                  ? "border-[var(--hexa-purple)] text-[var(--hexa-purple)]"
                  : "border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] hover:border-[var(--hexa-purple)]"
              }`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filtri
              {activeFiltersCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--hexa-purple)] text-white text-xs font-medium">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {/* Filter dropdown with animation */}
            {isFilterDropdownOpen && (
              <div
                className="task-filters absolute top-full left-0 sm:left-auto sm:right-0 mt-2 w-[272px] max-w-[calc(100vw-24px)] max-h-[60dvh] overflow-y-auto bg-[var(--hexa-surface)] border border-[var(--hexa-border)] rounded-[14px] shadow-lg z-50"
                style={{
                  animation: "filterDropdownIn 150ms ease-out forwards",
                }}
              >
                <style>{`
                  @keyframes filterDropdownIn {
                    from {
                      opacity: 0;
                      transform: translateY(-4px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                `}</style>

                {/* Priorità */}
                <div className="border-b border-[var(--hexa-border-subtle)]">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedFilter(expandedFilter === "priority" ? null : "priority")}
                    onKeyDown={(e) => e.key === "Enter" && setExpandedFilter(expandedFilter === "priority" ? null : "priority")}
                    className="w-full flex items-center justify-between p-3 hover:bg-[var(--hexa-surface-elevated)] transition cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-[var(--hexa-text-primary)]">Priorità</span>
                      {filterPriority.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-[var(--hexa-purple-glow)] text-[var(--hexa-purple)]">
                          {getFilterSummary(filterPriority, getPriorityLabel)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {filterPriority.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterPriority([]);
                            setExpandedFilter(null);
                          }}
                          className="p-0.5 hover:bg-[var(--hexa-surface)] rounded transition"
                        >
                          <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                        </button>
                      )}
                      <ChevronRight className={`h-4 w-4 text-[var(--hexa-text-muted)] transition-transform duration-150 ${expandedFilter === "priority" ? "rotate-90" : ""}`} />
                    </div>
                  </div>
                  {expandedFilter === "priority" && (
                    <div className="bg-[var(--hexa-surface-elevated)] border-t border-[var(--hexa-border-subtle)]">
                      {[
                        { value: "ALTA", label: "Alta", color: "#ef4444" },
                        { value: "MEDIA", label: "Media", color: "#f59e0b" },
                        { value: "BASSA", label: "Bassa", color: "#22c55e" }
                      ].map((option) => {
                        const isSelected = filterPriority.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => toggleFilterValue(filterPriority, setFilterPriority, option.value)}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] transition ${
                              isSelected
                                ? "bg-[var(--hexa-purple-glow)] text-[var(--hexa-text-primary)]"
                                : "text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: option.color }}
                              />
                              {option.label}
                            </div>
                            {isSelected && <Check className="h-4 w-4 text-[var(--hexa-purple)]" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Colonna (ex Stato) */}
                <div className="border-b border-[var(--hexa-border-subtle)]">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedFilter(expandedFilter === "status" ? null : "status")}
                    onKeyDown={(e) => e.key === "Enter" && setExpandedFilter(expandedFilter === "status" ? null : "status")}
                    className="w-full flex items-center justify-between p-3 hover:bg-[var(--hexa-surface-elevated)] transition cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-[var(--hexa-text-primary)]">Colonna</span>
                      {filterStatus.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-[var(--hexa-purple-glow)] text-[var(--hexa-purple)]">
                          {getFilterSummary(filterStatus, getStatusLabel)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {filterStatus.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterStatus([]);
                            setExpandedFilter(null);
                          }}
                          className="p-0.5 hover:bg-[var(--hexa-surface)] rounded transition"
                        >
                          <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                        </button>
                      )}
                      <ChevronRight className={`h-4 w-4 text-[var(--hexa-text-muted)] transition-transform duration-150 ${expandedFilter === "status" ? "rotate-90" : ""}`} />
                    </div>
                  </div>
                  {expandedFilter === "status" && (
                    <div className="bg-[var(--hexa-surface-elevated)] border-t border-[var(--hexa-border-subtle)] max-h-48 overflow-y-auto scrollbar-hexa">
                      {statusColumns.map((status) => {
                        const isSelected = filterStatus.includes(status.key);
                        return (
                          <button
                            key={status.key}
                            type="button"
                            onClick={() => toggleFilterValue(filterStatus, setFilterStatus, status.key)}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] transition ${
                              isSelected
                                ? "bg-[var(--hexa-purple-glow)] text-[var(--hexa-text-primary)]"
                                : "text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: status.color }}
                              />
                              {status.name}
                            </div>
                            {isSelected && <Check className="h-4 w-4 text-[var(--hexa-purple)]" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Assegnato a */}
                <div className="border-b border-[var(--hexa-border-subtle)]">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedFilter(expandedFilter === "assignedTo" ? null : "assignedTo")}
                    onKeyDown={(e) => e.key === "Enter" && setExpandedFilter(expandedFilter === "assignedTo" ? null : "assignedTo")}
                    className="w-full flex items-center justify-between p-3 hover:bg-[var(--hexa-surface-elevated)] transition cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-[var(--hexa-text-primary)]">Assegnato a</span>
                      {filterAssignedTo.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-[var(--hexa-purple-glow)] text-[var(--hexa-purple)]">
                          {getFilterSummary(filterAssignedTo, getAssigneeLabel)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {filterAssignedTo.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterAssignedTo([]);
                            setExpandedFilter(null);
                          }}
                          className="p-0.5 hover:bg-[var(--hexa-surface)] rounded transition"
                        >
                          <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                        </button>
                      )}
                      <ChevronRight className={`h-4 w-4 text-[var(--hexa-text-muted)] transition-transform duration-150 ${expandedFilter === "assignedTo" ? "rotate-90" : ""}`} />
                    </div>
                  </div>
                  {expandedFilter === "assignedTo" && (
                    <div className="bg-[var(--hexa-surface-elevated)] border-t border-[var(--hexa-border-subtle)] max-h-48 overflow-y-auto scrollbar-hexa">
                      {users
                        .filter((user) => user && typeof user.id === 'number' && typeof user.username === 'string')
                        .map((user, index) => {
                          const isSelected = filterAssignedTo.includes(String(user.id));
                          return (
                            <button
                              key={`assignedTo-${user.id}-${index}`}
                              type="button"
                              onClick={() => toggleFilterValue(filterAssignedTo, setFilterAssignedTo, String(user.id))}
                              className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] transition ${
                                isSelected
                                  ? "bg-[var(--hexa-purple-glow)] text-[var(--hexa-text-primary)]"
                                  : "text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <span
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: "var(--hexa-purple)" }}
                                />
                                {user.username}
                              </div>
                              {isSelected && <Check className="h-4 w-4 text-[var(--hexa-purple)]" />}
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>

                {/* Assegnato da */}
                <div className="border-b border-[var(--hexa-border-subtle)]">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedFilter(expandedFilter === "assignedBy" ? null : "assignedBy")}
                    onKeyDown={(e) => e.key === "Enter" && setExpandedFilter(expandedFilter === "assignedBy" ? null : "assignedBy")}
                    className="w-full flex items-center justify-between p-3 hover:bg-[var(--hexa-surface-elevated)] transition cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-[var(--hexa-text-primary)]">Assegnato da</span>
                      {filterAssignedBy.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-[var(--hexa-purple-glow)] text-[var(--hexa-purple)]">
                          {getFilterSummary(filterAssignedBy, (v) => v)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {filterAssignedBy.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterAssignedBy([]);
                            setExpandedFilter(null);
                          }}
                          className="p-0.5 hover:bg-[var(--hexa-surface)] rounded transition"
                        >
                          <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                        </button>
                      )}
                      <ChevronRight className={`h-4 w-4 text-[var(--hexa-text-muted)] transition-transform duration-150 ${expandedFilter === "assignedBy" ? "rotate-90" : ""}`} />
                    </div>
                  </div>
                  {expandedFilter === "assignedBy" && (
                    <div className="bg-[var(--hexa-surface-elevated)] border-t border-[var(--hexa-border-subtle)] max-h-48 overflow-y-auto scrollbar-hexa">
                      {users
                        .filter((user) => user && typeof user.id === 'number' && typeof user.username === 'string')
                        .map((user, index) => {
                          const isSelected = filterAssignedBy.includes(user.username);
                          return (
                            <button
                              key={`assignedBy-${user.id}-${index}`}
                              type="button"
                              onClick={() => toggleFilterValue(filterAssignedBy, setFilterAssignedBy, user.username)}
                              className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] transition ${
                                isSelected
                                  ? "bg-[var(--hexa-purple-glow)] text-[var(--hexa-text-primary)]"
                                  : "text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <span
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: "var(--hexa-purple)" }}
                                />
                                {user.username}
                              </div>
                              {isSelected && <Check className="h-4 w-4 text-[var(--hexa-purple)]" />}
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>

                {/* Scadenza */}
                <div className={hasActiveFilters ? "border-b border-[var(--hexa-border-subtle)]" : ""}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedFilter(expandedFilter === "dueDate" ? null : "dueDate")}
                    onKeyDown={(e) => e.key === "Enter" && setExpandedFilter(expandedFilter === "dueDate" ? null : "dueDate")}
                    className="w-full flex items-center justify-between p-3 hover:bg-[var(--hexa-surface-elevated)] transition cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-[var(--hexa-text-primary)]">Scadenza</span>
                      {filterDueDate.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-md text-[11px] font-medium bg-[var(--hexa-purple-glow)] text-[var(--hexa-purple)]">
                          {getFilterSummary(filterDueDate, getDueDateLabel)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {filterDueDate.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterDueDate([]);
                            setExpandedFilter(null);
                          }}
                          className="p-0.5 hover:bg-[var(--hexa-surface)] rounded transition"
                        >
                          <X className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
                        </button>
                      )}
                      <ChevronRight className={`h-4 w-4 text-[var(--hexa-text-muted)] transition-transform duration-150 ${expandedFilter === "dueDate" ? "rotate-90" : ""}`} />
                    </div>
                  </div>
                  {expandedFilter === "dueDate" && (
                    <div className="bg-[var(--hexa-surface-elevated)] border-t border-[var(--hexa-border-subtle)]">
                      {[
                        { value: "today", label: "Oggi", color: "#3b82f6" },
                        { value: "week", label: "Questa settimana", color: "#8b5cf6" },
                        { value: "overdue", label: "Scaduti", color: "#ef4444" }
                      ].map((option) => {
                        const isSelected = filterDueDate.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => toggleFilterValue(filterDueDate, setFilterDueDate, option.value)}
                            className={`w-full flex items-center justify-between px-4 py-2.5 text-[13px] transition ${
                              isSelected
                                ? "bg-[var(--hexa-purple-glow)] text-[var(--hexa-text-primary)]"
                                : "text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: option.color }}
                              />
                              {option.label}
                            </div>
                            {isSelected && <Check className="h-4 w-4 text-[var(--hexa-purple)]" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Azzera tutti i filtri */}
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={() => {
                      handleResetFilters();
                      setIsFilterDropdownOpen(false);
                    }}
                    className="w-full p-3 text-[13px] text-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-glow)] transition text-center font-medium border-t border-[var(--hexa-border-subtle)]"
                  >
                    Azzera tutti i filtri
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Toggle nascondi completati */}
          <button
            type="button"
            onClick={() => setHideCompleted(!hideCompleted)}
            aria-pressed={hideCompleted}
            className={`inline-flex min-h-10 items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              hideCompleted
                ? "bg-[var(--hexa-purple-glow)] border border-[var(--hexa-purple)] text-[var(--hexa-purple)]"
                : "bg-[var(--hexa-card-bg)] border border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] hover:border-[var(--hexa-purple)]"
            }`}
            title={hideCompleted ? "Mostra task completati" : "Nascondi task completati"}
          >
            {hideCompleted ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span>Completati</span>
          </button>

          {/* View toggle */}
          <div className="inline-flex ml-auto shrink-0 rounded-lg bg-[var(--hexa-card-bg)] border border-[var(--hexa-border)] p-[3px]">
            <button
              type="button"
              onClick={() => setView("kanban")}
              disabled={scope?.kind !== "project"}
              className={`w-10 h-9 rounded-md flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed ${
                scope?.kind === "project" && view === "kanban"
                  ? "bg-[var(--hexa-purple)] text-white"
                  : "text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)]"
              }`}
              title={scope?.kind === "project" ? "Griglia" : "Seleziona un progetto per usare il Kanban"}
              aria-label="Vista Kanban"
              aria-pressed={scope?.kind === "project" && view === "kanban"}
            >
              <LayoutGrid className="w-[15px] h-[15px]" />
            </button>
            <button
              type="button"
              onClick={() => setView("list")}
              className={`w-10 h-9 rounded-md flex items-center justify-center transition ${
                scope?.kind === "inbox" || view === "list"
                  ? "bg-[var(--hexa-purple)] text-white"
                  : "text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)]"
              }`}
              title="Lista"
              aria-label="Vista lista"
              aria-pressed={scope?.kind === "inbox" || view === "list"}
            >
              <List className="w-[15px] h-[15px]" />
            </button>
          </div>
        </div>
      </div>

      {scope?.kind === "inbox" && (
        <p className="text-xs text-[var(--hexa-text-muted)]">Seleziona un progetto per usare la vista Kanban.</p>
      )}

      {/* Summary bar — unified full-width.
          FE-4: usa scopeSummary server-side; skeleton durante cambio scope. */}
      <div className="task-summary flex flex-wrap items-center gap-x-4 gap-y-2 bg-[var(--hexa-card-bg)] border border-[var(--hexa-border)] hexa-card-shadow rounded-lg px-3 sm:px-5 py-2">
        {scopeSummaryLoading ? (
          <span className="text-[10px] text-[var(--hexa-text-muted)] italic">
            Aggiornamento statistiche...
          </span>
        ) : (
          <>
            <span className="text-[9px] text-[var(--hexa-text-muted)] uppercase tracking-wider font-semibold">Task</span>
            <div className="w-px h-5 bg-[var(--hexa-border)]" />
            <div className="flex items-center gap-1.5">
              <span className="text-base font-bold text-[var(--hexa-text-primary)]">{total}</span>
              <span className="text-[9px] text-[var(--hexa-text-muted)]">Totali</span>
            </div>
            <div className="w-px h-5 bg-[var(--hexa-border)]" />
            <div className="flex items-center gap-1.5">
              <span className="text-base font-bold text-[var(--hexa-text-primary)]">{completed}</span>
              <span className="text-[9px] text-[var(--hexa-text-muted)]">Completati</span>
            </div>
            <div className="w-px h-5 bg-[var(--hexa-border)]" />
            <div className="flex items-center gap-1.5">
              <span className="text-base font-bold text-[var(--hexa-text-primary)]">{overdue}</span>
              <span className="text-[9px] text-[var(--hexa-text-muted)]">Scaduti</span>
            </div>
            <div className="w-px h-5 bg-[var(--hexa-border)]" />
            <span className="text-[9px] text-[var(--hexa-text-muted)] uppercase tracking-wider font-semibold">Priorità</span>
            <div className="w-px h-5 bg-[var(--hexa-border)]" />
            <div className="flex items-center gap-1.5">
              <div className="w-[5px] h-[5px] rounded-full flex-shrink-0 bg-[#ef4444]" />
              <span className="text-base font-bold text-[var(--hexa-text-primary)]">{urgent}</span>
              <span className="text-[9px] text-[var(--hexa-text-muted)]">Urgente</span>
            </div>
            <div className="w-px h-5 bg-[var(--hexa-border)]" />
            <div className="flex items-center gap-1.5">
              <div className="w-[5px] h-[5px] rounded-full flex-shrink-0 bg-[#f59e0b]" />
              <span className="text-base font-bold text-[var(--hexa-text-primary)]">{medium}</span>
              <span className="text-[9px] text-[var(--hexa-text-muted)]">Medio</span>
            </div>
            <div className="w-px h-5 bg-[var(--hexa-border)]" />
            <div className="flex items-center gap-1.5">
              <div className="w-[5px] h-[5px] rounded-full flex-shrink-0 bg-[#22c55e]" />
              <span className="text-base font-bold text-[var(--hexa-text-primary)]">{low}</span>
              <span className="text-[9px] text-[var(--hexa-text-muted)]">Bassa</span>
            </div>
          </>
        )}
      </div>

      {/* Task views */}
      {loading ? (
        <div className="text-center text-[var(--hexa-text-secondary)] py-20">Caricamento task...</div>
      ) : scope?.kind === "inbox" ? (
        // Inbox: sempre vista lista, nessun Kanban. Mostra CTA "Assegna a progetto".
        filteredTasks.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-sm text-[var(--hexa-text-secondary)]">
              Non ci sono task da organizzare.
            </p>
            <button
              type="button"
              onClick={handleCreateTaskFromHeader}
              className="mt-3 inline-flex items-center h-7 px-3 rounded-md bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white text-sm font-medium transition"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Crea task
            </button>
          </div>
        ) : (
          <TaskListView
            tasks={filteredTasks}
            onOpenTask={handleOpenTask}
            onDeleteTask={handleDeleteTask}
            onToggleCompleted={handleToggleCompleted}
            highlightedTaskId={highlightedTaskId}
            showProjectColumn
            onAssignToProject={handleAssignToProject}
            onMoveTaskToColumn={handleMoveTaskToColumn}
            onMoveTaskToProject={handleMoveTaskToProject}
          />
        )
      ) : view === "list" ? (
        <TaskListView
          tasks={filteredTasks}
          onOpenTask={handleOpenTask}
          onDeleteTask={handleDeleteTask}
          onToggleCompleted={handleToggleCompleted}
          highlightedTaskId={highlightedTaskId}
          // Nella vista progetto la colonna Progetto è ridondante (tutti stessi progetto).
          showProjectColumn={false}
          onAssignToProject={handleAssignToProject}
          onMoveTaskToColumn={handleMoveTaskToColumn}
          onMoveTaskToProject={handleMoveTaskToProject}
        />
      ) : scope?.kind === "project" && statusColumns.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-sm text-[var(--hexa-text-secondary)]">
            Il progetto non ha colonne. Crea la prima colonna per iniziare.
          </p>
        </div>
      ) : filteredTasks.length === 0 && statusColumns.length > 0 ? (
        <div className="text-center py-20">
          <p className="text-sm text-[var(--hexa-text-secondary)]">
            Questo progetto non contiene ancora task.
          </p>
          <button
            type="button"
            onClick={handleCreateTaskFromHeader}
            className="mt-3 inline-flex items-center h-7 px-3 rounded-md bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white text-sm font-medium transition"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Crea il primo task
          </button>
        </div>
      ) : (
        <TaskKanbanView
          tasks={filteredKanbanTasks}
          statusColumns={statusColumns}
          columnStates={columnStates}
          onLoadMore={loadMoreTasks}
          onOpenTask={handleOpenTask}
          onCreateTask={handleCreateTask}
          onTaskMove={handleTaskMove}
          onColumnOrderChange={handleColumnOrderChange}
          onRenameStatus={handleRenameStatus}
          onUpdateColumn={handleUpdateColumn}
          onDeleteEmptyColumn={handleDeleteEmptyColumn}
          onMoveAndDeleteColumn={handleMoveAndDeleteColumn}
          availableColumns={statusColumns}
          onAddColumn={handleAddColumn}
          onDeleteTask={handleDeleteTask}
          onToggleCompleted={handleToggleCompleted}
          onMoveTaskToColumn={handleMoveTaskToColumn}
          onMoveTaskToProject={handleMoveTaskToProject}
          isManager={isManager}
          highlightedTaskId={highlightedTaskId}
        />
      )}

      {/* Task modal */}
      <Sheet open={isNewTaskModalOpen} onOpenChange={(open) => !open && handleModalClose()}>
        <SheetContent className="w-full sm:max-w-[380px] h-[100dvh] max-h-[100dvh] p-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)] overflow-hidden">
          <SheetTitle className="sr-only">Nuovo Task</SheetTitle>
          <SheetDescription className="sr-only">Crea un nuovo task</SheetDescription>
          <NewTaskModal
            isOpen={isNewTaskModalOpen}
            onClose={handleModalClose}
            onSubmit={handleTaskSaved}
            onFetchTaskHistory={fetchTaskHistory}
            currentTask={currentTask}
            statusColumns={statusColumns}
            users={users}
            sessionKey={sessionKey}
            defaultStatus={defaultStatus}
            availableContacts={availableContacts}
            // FE-4 §6.5: prefill project/status dallo scope corrente e passa
            // la lista progetti attivi per la select Progetto.
            initialProjectId={initialProjectId}
            initialStatusId={initialStatusId}
            availableProjects={activeProjectsHook.projects}
          />
        </SheetContent>
      </Sheet>

      {/* FE-3/4: manager sheet + form progetti (integrazione container) */}
      <ProjectManagerSheet
        open={projectSheetOpen}
        onOpenChange={setProjectSheetOpen}
        activeProjects={activeProjectsHook.projects}
        archivedProjects={archivedProjectsHook.projects}
        loading={activeProjectsHook.loading || archivedProjectsHook.loading}
        currentProjectId={scope?.kind === "project" ? scope.projectId : null}
        onEditProject={handleOpenEditProject}
        onCurrentProjectArchived={handleCurrentProjectArchived}
        onProjectsChanged={async () => {
          await Promise.all([
            activeProjectsHook.refetch(),
            archivedProjectsHook.refetch(),
          ]);
        }}
      />

      <ProjectForm
        open={projectFormOpen}
        onOpenChange={(open) => {
          setProjectFormOpen(open);
          if (!open) setEditingProject(null);
        }}
        project={editingProject}
        copySourceProjects={activeProjectsHook.projects}
        onSubmit={async (payload, isEdit) => {
          try {
            await handleProjectSubmit(payload, isEdit);
          } catch (err) {
            // L'errore viene normalizzato e mostrato inline dal form.
            throw err;
          }
        }}
      />

      {/* FE-5 §6.4: MoveTaskDialog per spostamenti accessibili */}
      <MoveTaskDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        task={moveDialogTask}
        availableProjects={activeProjectsHook.projects}
        mode={moveDialogMode}
        currentProjectId={scope?.kind === "project" ? scope.projectId : null}
        onSubmit={handleMoveTaskSubmit}
      />
    </div>
  );
}

